import { useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  Bell, Compass, Briefcase, GameController, GraduationCap, Handshake,
  ChatCircleDots, X, Buildings, Storefront,
} from "@phosphor-icons/react";

/* Inbox slide-over drawer.
 * Hosts the 7 secondary hubs so the top nav can stay lean.
 * Controlled by `open` + `onClose` from AppShell. */
const HUBS = [
  { to: "/alerts",       label: "Alerts",           icon: Bell,            color: "text-yellow-300",  testId: "inbox-hub-alerts" },
  { to: "/inbox",        label: "Chats",            icon: ChatCircleDots,  color: "text-blue-400",    testId: "inbox-hub-chats" },
  { to: "/market",       label: "Tainapp Market",   icon: Storefront,      color: "text-sky-300",     testId: "inbox-hub-market" },
  { to: "/discover",     label: "Explore Hub",      icon: Compass,         color: "text-fuchsia-400", testId: "inbox-hub-explore" },
  { to: "/jobs",         label: "Jobs Board",       icon: Briefcase,       color: "text-amber-400",   testId: "inbox-hub-jobs" },
  { to: "/games",        label: "Academic Games",   icon: GameController,  color: "text-emerald-400", testId: "inbox-hub-games" },
  { to: "/schools",      label: "Schools Hub",      icon: GraduationCap,   color: "text-indigo-400",  testId: "inbox-hub-schools" },
  { to: "/partnerships", label: "Business Partners", icon: Handshake,      color: "text-teal-400",    testId: "inbox-hub-partners" },
  { to: "/messages",     label: "Instant Messages", icon: ChatCircleDots,  color: "text-sky-400",     testId: "inbox-hub-messages" },
];

export default function InboxDrawer({ open, onClose, unreadCount = 0 }) {
  // Lock body scroll when drawer open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        aria-hidden={!open}
        className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      {/* Drawer */}
      <aside
        role="dialog"
        aria-label="Inbox drawer"
        data-testid="inbox-drawer"
        aria-hidden={!open}
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-[#0a0a0a] border-l border-white/10 z-[70] flex flex-col transition-transform duration-200 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h2 className="font-heading text-xl font-black flex items-center gap-2">
              <Buildings size={22} weight="fill" className="text-emerald-400" />
              Inbox
            </h2>
            <p className="text-xs text-white/50 mt-0.5">Alerts · hubs · messages</p>
          </div>
          <button
            onClick={onClose}
            data-testid="inbox-close-btn"
            aria-label="Close inbox"
            className="p-2 rounded-md hover:bg-white/10"
          >
            <X size={20} weight="bold" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {HUBS.map(h => {
            const Icon = h.icon;
            const isAlerts = h.to === "/alerts";
            return (
              <NavLink
                key={h.to}
                to={h.to}
                onClick={onClose}
                data-testid={h.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 p-3 rounded-lg border transition ${
                    isActive
                      ? "bg-emerald-500/10 border-emerald-500/40"
                      : "bg-white/[0.03] border-white/10 hover:border-white/25 hover:bg-white/5"
                  }`
                }
              >
                <div className={`w-10 h-10 rounded-lg bg-black/40 flex items-center justify-center ${h.color}`}>
                  <Icon size={20} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold">{h.label}</div>
                </div>
                {isAlerts && unreadCount > 0 && (
                  <span
                    data-testid="inbox-alerts-badge"
                    className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-black flex items-center justify-center"
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>

        <footer className="px-5 py-3 border-t border-white/10 text-[11px] text-white/40">
          © 2026 Tainapp Inc.
        </footer>
      </aside>
    </>
  );
}
